import { EmptyState } from "src/components/EmptyState"
import { ParadiseBarChart } from "src/components/Paradise/ParadiseCharts/ParadiseBarChart"
import { ParadiseLineChart } from "src/components/Paradise/ParadiseCharts/ParadiseLineChart"
import { useParadiseDeviceReadings } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/useParadiseDeviceReadings"
import { LoadingWrapper } from "src/components/Paradise/sharedStyles"
import {
  DeviceReadingType,
  IDeviceMotionReadings,
} from "src/data/devices/types/deviceTypes"
import { TGraphZoomOption } from "src/ui/Graphs/graphTypes"
import { MCircularProgress } from "src/ui/MCircularProgress/MCircularProgress"
import { utcToHomeTimezone } from "src/utils/l10n"

function convertMotionReadingsToHomeTimezone(
  readings: IDeviceMotionReadings["values"],
  useHomeTimezone: boolean,
  homeTimezone?: string
): IDeviceMotionReadings["values"] {
  return readings.map(([timestamp, value]) => {
    const date = new Date(timestamp)
    return [
      useHomeTimezone && homeTimezone
        ? utcToHomeTimezone(date, homeTimezone).getTime()
        : timestamp,
      value,
    ] as [number, number]
  })
}

function convertReadingsToHomeTimezone<T extends { datetime: string | Date }>(
  readings: T[],
  useHomeTimezone: boolean,
  homeTimezone?: string
): T[] {
  return readings.map((reading) => ({
    ...reading,
    datetime:
      useHomeTimezone && homeTimezone
        ? utcToHomeTimezone(reading.datetime, homeTimezone).getTime()
        : reading.datetime,
  }))
}

export function ParadiseDeviceReadingsGraph({
  deviceId,
  type,
  dateRange,
  useHomeTimezone,
  homeTimezone,
  zoom,
}: {
  deviceId: string
  type: DeviceReadingType
  dateRange: { startAt: Date; endAt: Date }
  useHomeTimezone?: boolean
  homeTimezone?: string
  zoom?: TGraphZoomOption
}) {
  const deviceReadings = useParadiseDeviceReadings({
    deviceId,
    type,
    dateRange,
  })

  if (deviceReadings.isLoading) {
    return (
      <LoadingWrapper>
        <MCircularProgress />
      </LoadingWrapper>
    )
  }

  if (
    type === "motion_events" &&
    hasReadings(deviceReadings.motionReadings?.values)
  ) {
    const motionData = convertMotionReadingsToHomeTimezone(
      deviceReadings.motionReadings.values,
      !!useHomeTimezone,
      homeTimezone
    )
    return (
      <ParadiseBarChart
        data={motionData}
        tooltip={{ unit: "" }}
        canExport
        zoom={zoom}
      />
    )
  }

  if (hasReadings(deviceReadings.dashboardReadings)) {
    const dashboardData = convertReadingsToHomeTimezone(
      deviceReadings.dashboardReadings,
      !!useHomeTimezone,
      homeTimezone
    )
    return (
      <ParadiseLineChart
        data={dashboardData}
        tooltip={{ unit: "" }}
        canExport
        smooth={false}
        zoom={zoom}
      />
    )
  }

  if (hasReadings(deviceReadings.readings?.values)) {
    const readingsData = convertReadingsToHomeTimezone(
      deviceReadings.readings.values,
      !!useHomeTimezone,
      homeTimezone
    )

    return (
      <ParadiseLineChart
        data={readingsData}
        tooltip={{ unit: "" }}
        canExport
        smooth={false}
        zoom={zoom}
      />
    )
  }

  return <EmptyState icon={<></>} title="No readings available" />
}

function hasReadings<Type extends unknown[]>(
  readings: Type | undefined
): readings is Type {
  if (readings && readings.length > 0) {
    return true
  }

  return false
}
